import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

const contextOptions = [
  { value: "GENERAL", label: "Marketplace support" },
  { value: "SOURCING", label: "Sourcing assistant" },
  { value: "BUYER", label: "Buyer help" },
  { value: "SELLER", label: "Seller help" },
  { value: "TRANSPORT", label: "Transport help" },
  { value: "FINANCE", label: "Payments & settlements" },
  { value: "ADMIN", label: "Operations review" },
];

const staffTypes = ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"];

function isStaffUser(user) {
  const type = String(user?.account_type || "").toUpperCase();
  return staffTypes.includes(type) || user?.is_platform_staff || user?.is_superuser;
}

function getAllowedContextOptions(user) {
  const type = String(user?.account_type || "").toUpperCase();
  if (isStaffUser(user)) return contextOptions;
  if (type === "SELLER" || type === "BUSINESS") {
    return contextOptions.filter((option) => ["GENERAL", "SOURCING", "SELLER", "TRANSPORT", "FINANCE"].includes(option.value));
  }
  if (type === "COURIER") {
    return contextOptions.filter((option) => ["GENERAL", "SOURCING", "TRANSPORT"].includes(option.value));
  }
  return contextOptions.filter((option) => ["GENERAL", "SOURCING", "BUYER", "TRANSPORT"].includes(option.value));
}

function getInitialContext(user) {
  const type = String(user?.account_type || "").toUpperCase();
  if (isStaffUser(user)) return "ADMIN";
  if (type === "SELLER" || type === "BUSINESS") return "SELLER";
  if (type === "COURIER") return "TRANSPORT";
  if (type === "BUYER") return "BUYER";
  return "GENERAL";
}

function fallbackPrompts(user) {
  const type = String(user?.account_type || "").toUpperCase();
  if (isStaffUser(user)) {
    return [
      "Summarise pending marketplace operations that need attention.",
      "Which documents, sellers, transporters or payments should be reviewed first?",
      "Explain the current marketplace order and fulfilment position.",
    ];
  }
  if (type === "SELLER" || type === "BUSINESS") {
    return [
      "Which enquiries or orders need my attention?",
      "Help improve my product listing descriptions.",
      "Explain my settlement and subscription position.",
    ];
  }
  if (type === "COURIER") {
    return [
      "Which delivery requests need my action?",
      "Help me describe my transport service professionally.",
      "Explain what I should do next on active delivery jobs.",
    ];
  }
  return [
    "Help me find products and sellers for what I need.",
    "Explain my latest order status in simple terms.",
    "Which of my payments are still pending?",
  ];
}

function usageLabel(usage = {}) {
  if (usage.limit === null || usage.limit === undefined) return `${usage.this_month || 0} messages this month`;
  return `${usage.this_month || 0}/${usage.limit} messages used`;
}

export default function AIWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [searchParams] = useSearchParams();
  const [contextType, setContextType] = useState(() => (searchParams.get("context") || getInitialContext(user)).toUpperCase());
  const [message, setMessage] = useState(searchParams.get("prompt") || "");
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summary, setSummary] = useState({ suggestions: [], workflow: [], usage: {}, entitlement: {} });
  const [error, setError] = useState("");

  const allowedContextOptions = useMemo(() => getAllowedContextOptions(user), [user]);
  const quickPrompts = useMemo(() => (summary.suggestions?.length ? summary.suggestions : fallbackPrompts(user)), [summary.suggestions, user]);

  useEffect(() => {
    let mounted = true;
    async function loadSummary() {
      setSummaryLoading(true);
      try {
        const res = await phase1Api.aiSummary();
        if (mounted) setSummary(res.data || { suggestions: [], workflow: [], usage: {}, entitlement: {} });
      } catch (err) {
        if (mounted) setSummary({ suggestions: fallbackPrompts(user), workflow: [], usage: {}, entitlement: {} });
      } finally {
        if (mounted) setSummaryLoading(false);
      }
    }
    loadSummary();
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    if (!allowedContextOptions.some((option) => option.value === contextType)) {
      setContextType(getInitialContext(user));
    }
  }, [allowedContextOptions, contextType, user]);

  async function sendMessage(event, presetText) {
    if (event?.preventDefault) event.preventDefault();
    const clean = String(presetText || message || "").trim();
    if (!clean) return;
    setLoading(true);
    setError("");
    const optimistic = { sender: "USER", content: clean, id: `local-${Date.now()}` };
    setMessages((current) => [...current, optimistic]);
    setMessage("");
    try {
      const res = await phase1Api.aiAssist({ message: clean, context_type: contextType, conversation_id: conversationId });
      setConversationId(res.data?.conversation?.id || conversationId);
      const assistant = res.data?.assistant_message || { sender: "ASSISTANT", content: res.data?.reply || "No response." };
      setMessages((current) => [...current, assistant]);
      if (res.data?.entitlement) {
        setSummary((current) => ({
          ...current,
          entitlement: res.data.entitlement,
          usage: {
            this_month: res.data.entitlement.ai_used_this_month,
            limit: res.data.entitlement.ai_limit,
            remaining: res.data.entitlement.ai_remaining,
          },
        }));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.reply || "TAZY AI could not complete the request. Please try again.";
      if (err?.response?.data?.entitlement) {
        setSummary((current) => ({ ...current, entitlement: err.response.data.entitlement }));
      }
      setError(detail);
      setMessages((current) => [...current, { sender: "ASSISTANT", content: detail, status: "ERROR", id: `error-${Date.now()}` }]);
    } finally {
      setLoading(false);
    }
  }

  const workflow = summary.workflow || [];
  const usage = summary.usage || {};
  const entitlement = summary.entitlement || {};
  const plan = entitlement.plan;

  return (
    <div className={`portal-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main refined-portal-main">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="portal-hero ai-hero-panel ai-hero-panel--focused">
          <div>
            <span>TAZY AI</span>
            <h1>Marketplace assistant</h1>
            <p>Ask for role-safe help with products, orders, enquiries, deliveries, payments and next steps.</p>
          </div>
          <div className="ai-usage-pill">
            <strong>{usageLabel(usage)}</strong>
            <span>{plan?.name ? plan.name : entitlement.status === "NO_ACTIVE_SUBSCRIPTION" ? "No active package" : "Role controlled"}</span>
          </div>
        </section>

        {workflow.length > 0 && (
          <section className="ai-workflow-strip">
            {workflow.map((item) => (
              <button type="button" key={`${item.title}-${item.prompt}`} onClick={(event) => sendMessage(event, item.prompt)} disabled={loading}>
                <strong>{item.count ?? 0}</strong>
                <span>{item.title}</span>
              </button>
            ))}
          </section>
        )}

        <section className="ai-workspace-grid">
          <aside className="ai-control-card">
            <div className="ai-section-title">
              <span>Mode</span>
              <h2>Assistant scope</h2>
            </div>
            <select value={contextType} onChange={(event) => setContextType(event.target.value)}>
              {allowedContextOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>

            <div className="ai-section-title ai-spaced-title">
              <span>Prompts</span>
              <h2>Quick actions</h2>
            </div>
            <div className="ai-quick-list">
              {quickPrompts.map((item) => (
                <button type="button" key={item} onClick={(event) => sendMessage(event, item)} disabled={loading}>{item}</button>
              ))}
            </div>

            <div className="ai-package-note">
              <strong>Package access</strong>
              <p>{summaryLoading ? "Checking access..." : entitlement.message || "Role permissions control what TAZY AI can answer."}</p>
            </div>
          </aside>

          <section className="ai-chat-card">
            <div className="ai-chat-header">
              <div>
                <span>Assistant</span>
                <h2>Ask TAZY AI</h2>
              </div>
              <button type="button" onClick={() => { setMessages([]); setConversationId(null); }}>New chat</button>
            </div>

            <div className="ai-message-window">
              {messages.length === 0 ? (
                <div className="ai-empty-state">
                  <strong>Ready to assist</strong>
                  <p>Use TAZY AI for marketplace guidance and next-step support allowed for your account.</p>
                </div>
              ) : messages.map((item) => (
                <article key={item.id || `${item.sender}-${item.created_at}-${item.content?.slice(0, 10)}`} className={`ai-message-row ${item.sender === "USER" ? "from-user" : "from-ai"}`}>
                  <strong>{item.sender === "USER" ? "You" : "TAZY AI"}</strong>
                  <p>{item.content}</p>
                </article>
              ))}
              {loading && <div className="ai-message-row from-ai is-loading"><strong>TAZY AI</strong><p>Thinking...</p></div>}
            </div>

            {error && <div className="ai-error-strip">{error}</div>}

            <form className="ai-compose-form" onSubmit={sendMessage}>
              <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Ask about products, sellers, transport, orders, payments, stock status or marketplace workflow..." rows={3} />
              <button type="submit" disabled={loading || !message.trim()}>{loading ? "Sending..." : "Ask AI"}</button>
            </form>
          </section>
        </section>
      </main>
    </div>
  );
}
